import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import './Auth.css'

export function ResetPassword() {
  const navigate = useNavigate()
  const [form, setForm]         = useState({ password: '', confirm: '' })
  const [errors, setErrors]     = useState({})
  const [serverError, setServerError] = useState('')
  const [loading, setLoading]   = useState(false)
  const [done, setDone]         = useState(false)
  const [validSession, setValidSession] = useState(false)
  const [checking, setChecking] = useState(true)

  useEffect(() => {
    // Supabase processes the #access_token hash from the reset email link.
    // Two paths:
    //   A) Token not yet consumed — onAuthStateChange fires PASSWORD_RECOVERY
    //   B) Token already consumed (e.g. StrictMode double-mount, slow render) —
    //      getSession() returns an active session directly
    // Both paths set validSession = true. A ref guards against double-handling.
    let handled = false

    // Path B: check for an existing session immediately on mount.
    // If Supabase already consumed the hash before the listener registered, this catches it.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (handled) return
      if (session) {
        handled = true
        setValidSession(true)
        setChecking(false)
      }
    })

    // Path A: listen for the auth event (normal flow)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (handled) return
        if (event === 'PASSWORD_RECOVERY') {
          handled = true
          setValidSession(true)
          setChecking(false)
        } else if (event === 'SIGNED_IN' && session && window.location.hash.includes('type=recovery')) {
          // Fallback: recovery SIGNED_IN event (Supabase v2 may emit this instead)
          handled = true
          setValidSession(true)
          setChecking(false)
        }
      }
    )

    // Final fallback timeout — if nothing fires within 5s, assume expired link
    const timeout = setTimeout(() => {
      if (!handled) setChecking(false)
    }, 5000)

    return () => {
      subscription.unsubscribe()
      clearTimeout(timeout)
    }
  }, [])

  function handleChange(e) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
    setErrors(prev => ({ ...prev, [e.target.name]: '' }))
    setServerError('')
  }

  function validate() {
    const errs = {}
    if (form.password.length < 8) errs.password = 'Minimum 8 characters.'
    if (form.password !== form.confirm) errs.confirm = 'Passwords do not match.'
    return errs
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }

    setLoading(true)
    setServerError('')

    try {
      const { error } = await supabase.auth.updateUser({
        password: form.password,
      })

      if (error) {
        setServerError('Something went wrong. Your reset link may have expired — request a new one.')
        setLoading(false)
        return
      }

      setDone(true)
      // Sign out and redirect to login — clean state after password reset
      setTimeout(async () => {
        try { await supabase.auth.signOut() } catch (_) { /* ignore */ }
        navigate('/login', { replace: true })
      }, 2500)
    } catch (e) {
      console.error('ResetPassword: updateUser threw:', e)
      setServerError('Network error. Check your connection and try again.')
      setLoading(false)
    }
  }

  // Loading state while checking session
  if (checking) {
    return (
      <div className="auth-shell">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <div style={{
            width: 28, height: 28,
            border: '2px solid rgba(255,255,255,0.1)',
            borderTopColor: '#e8ff47', borderRadius: '50%',
            animation: 'spin 0.6s linear infinite',
          }} />
        </div>
      </div>
    )
  }

  // Invalid or expired link
  if (!validSession) {
    return (
      <div className="auth-shell">
        <div className="auth-card auth-card--confirm">
          <a href={process.env.REACT_APP_SITE_URL || 'https://techbench.it'} className="auth-logo" style={{ textDecoration: 'none', justifyContent: 'center' }}>
            <div className="auth-logo__mark">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
                <rect x="1" y="4" width="14" height="9" rx="1.5" stroke="#0a0c10" strokeWidth="1.5"/>
                <path d="M5 8h6M5 10.5h3" stroke="#0a0c10" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M6 2h4" stroke="#0a0c10" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <span className="auth-logo__text">TechBench</span>
          </a>
          <h1 className="auth-title">Link expired</h1>
          <p className="auth-subtitle">
            This password reset link is no longer valid. Request a new one below.
          </p>
          <Link to="/forgot-password">
            <Button fullWidth>Request new link</Button>
          </Link>
          <Link to="/login">
            <Button fullWidth variant="secondary">Back to sign in</Button>
          </Link>
        </div>
      </div>
    )
  }

  // Success state
  if (done) {
    return (
      <div className="auth-shell">
        <div className="auth-card auth-card--confirm">
          <a href={process.env.REACT_APP_SITE_URL || 'https://techbench.it'} className="auth-logo" style={{ textDecoration: 'none', justifyContent: 'center' }}>
            <div className="auth-logo__mark">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
                <rect x="1" y="4" width="14" height="9" rx="1.5" stroke="#0a0c10" strokeWidth="1.5"/>
                <path d="M5 8h6M5 10.5h3" stroke="#0a0c10" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M6 2h4" stroke="#0a0c10" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <span className="auth-logo__text">TechBench</span>
          </a>
          <h1 className="auth-title">Password updated.</h1>
          <p className="auth-subtitle">
            Your password has been changed. Signing you out for security — please sign in with your new password.
          </p>
        </div>
      </div>
    )
  }

  // Reset form
  return (
    <div className="auth-shell">
      <div className="auth-card">
        <a href={process.env.REACT_APP_SITE_URL || 'https://techbench.it'} className="auth-logo" style={{ textDecoration: 'none' }}>
          <div className="auth-logo__mark">
            <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
              <rect x="1" y="4" width="14" height="9" rx="1.5" stroke="#0a0c10" strokeWidth="1.5"/>
              <path d="M5 8h6M5 10.5h3" stroke="#0a0c10" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M6 2h4" stroke="#0a0c10" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <span className="auth-logo__text">TechBench</span>
        </a>

        <h1 className="auth-title">Set a new password</h1>
        <p className="auth-subtitle">Choose something strong. Minimum 8 characters.</p>

        {serverError && <div className="auth-error" role="alert">{serverError}</div>}

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <Input
            id="password" name="password" type="password" label="New password"
            value={form.password} onChange={handleChange} error={errors.password}
            hint="Minimum 8 characters" required autoFocus autoComplete="new-password"
          />
          <Input
            id="confirm" name="confirm" type="password" label="Confirm new password"
            value={form.confirm} onChange={handleChange} error={errors.confirm}
            required autoComplete="new-password"
          />
          <Button type="submit" fullWidth loading={loading} size="lg">
            Update password
          </Button>
        </form>
      </div>
    </div>
  )
}
